<template>
  <v-card outlined>
    <v-card-title class="align-start">
      <v-row>
        <v-col cols="12"> Résultats </v-col>
      </v-row>
      <dot-menu
        :isLoading="page.loading"
        @exportExcel="exportExcel"
        :excelButton="{
          excelData: excelButton.data,
          filename: `inventaire_video-${startDate}_${endDate}.xlsx`,
          disabled: exportDisabled,
        }"
      />
    </v-card-title>
    <v-card-text>
      <v-data-table
        :headers="page.headers"
        :items="page.items"
        :loading="page.loading"
        :hide-default-footer="true"
        :disable-pagination="true"
        :disable-filtering="true"
        :sort-by="'site'"
      />
      <!-- :disable-sort="true" -->
    </v-card-text>
    <progress-bar :download="download" />
  </v-card>
</template>

<script>
import { computed, onMounted, reactive, watch } from "@vue/composition-api";

import DotMenu from "@/components/common/menus/DotMenu";
import ProgressBar, {
  download_initial_state,
  resetDownload,
} from "@/components/common/ProgressBar";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";

import { formatNumber, formatPercent } from "@/utils/formatting";

export default {
  name: "Results",
  components: {
    DotMenu,
    ProgressBar,
  },
  setup(props, { emit }) {
    const { store } = useProxy();
    const { axiosGet } = useAxios();

    const page = reactive({
      loading: false,
      headers: [],
      items: [],
      data: [], // grouped by month. For non grouped data, just sum results by sites.
    });

    const download = reactive(download_initial_state());

    const excelButton = reactive({
      data: new Blob([""], { type: "text/plain" }),
    });

    // =>  METHODS
    const getHeaders = () => {
      const headers = [
        {
          text: "Site",
          align: "start",
          value: "site",
        },
      ];

      if (groupByMonth.value) {
        headers.push({ text: "Mois", value: "month" });
      }

      return headers.concat([
        {
          text: "Monetizable plays",
          value: "monetizable",
        },
        {
          text: "Plays",
          value: "plays",
        },
        {
          text: "Ratio",
          value: "ratio",
        },
      ]);
    };

    const checkDates = () => {
      if (
        !startDate.value ||
        startDate.value.length !== 10 ||
        !endDate.value ||
        endDate.value.length !== 10
      ) {
        alert("Sélectionnez une plage de date !");
        return false;
      }

      return true;
    };

    const formatResult = (data) =>
      data.map((item) => ({
        ...item,
        monetizable: formatNumber(item.monetizable),
        plays: formatNumber(item.plays),
        ratio: formatPercent(item.ratio),
      }));

    const aggregateData = (data) => {
      const result = [];
      for (const [site_name, item] of Object.entries(data)) {
        for (const [month_name, month_item] of Object.entries(item)) {
          result.push({
            site: site_name,
            month: month_name,
            monetizable: month_item.monetizable,
            plays: month_item.plays,
            ratio: month_item.ratio,
          });
        }
      }

      return result;
    };

    const aggregateItems = () => {
      if (groupByMonth.value) {
        return formatResult(page.data);
      }

      const result = {};
      for (const item of page.data) {
        if (!(item.site in result)) {
          result[item.site] = item;
        } else {
          result[item.site].monetizable += item.monetizable;
          result[item.site].plays += item.plays;
          result[item.site].ratio =
            result[item.site].plays /
            Math.max(1, result[item.site].monetizable);
        }
      }

      return formatResult(Object.values(result));
    };

    const getData = async () => {
      if (applyFilters.value && !page.loading && checkDates()) {
        page.loading = true;
        const { data } = await axiosGet("/regie/video-inventory", {
          start_date: startDate.value,
          end_date: endDate.value,
          group_by_month: true,
        });

        page.data = aggregateData(data);
        page.items = aggregateItems();

        page.loading = false;

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        store.dispatch("common/updateApplyFilters", false);
      }
    };

    const exportExcel = async () => {
      if (!checkDates()) {
        return;
      }

      page.loading = true;
      download.running = true;

      try {
        const { data } = await axiosGet(
          "/regie/video-inventory/export_xlsx",
          {
            start_date: startDate.value,
            end_date: endDate.value,
            group_by_month: groupByMonth.value,
          },
          {
            responseType: "blob",
            onDownloadProgress: (progressEvent) => {
              download.percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            },
          }
        );

        excelButton.data = data;
      } catch (error) {
        console.error("error while fetching data");
        resetDownload(download);
      }

      page.loading = false;
    };

    // =>  COMPUTED
    const applyFilters = computed(
      () => store.getters["common/getApplyFilters"]
    );
    const startDate = computed(() => store.getters["regie/getDates"][0]);
    const endDate = computed(() => store.getters["regie/getDates"][1]);

    const exportDisabled = computed(
      () => page.loading || page.items.length === 0
    );

    const groupByMonth = computed(() => store.getters["regie/getGroupByMonth"]);

    // =>  WATCH
    watch(applyFilters, () => {
      getData();
    });

    watch(groupByMonth, () => {
      page.items = aggregateItems();
      page.headers = getHeaders();
    });

    watch(
      () => download.percentCompleted,
      (newValue) => {
        if (newValue >= 100) {
          setTimeout(() => {
            resetDownload(download);
          }, 500);
        }
      }
    );

    // =>  HOOKS
    onMounted(() => {
      page.headers = getHeaders();
    });

    // =>  DATA
    return {
      page,
      download,
      exportExcel,
      excelButton,
      exportDisabled,
      startDate,
      endDate,
    };
  },
};
</script>

<style></style>
